/* ============================================================
   GOLDEN STAR JEWELLERY — STAR BADGE INSIDE THE NIVODA PANEL
   File: assets/gsj-nivoda-review-badge.js

   Places the 4.9/5 star badge between "Style …" and the "Share"
   control in the Nivoda app's own setting-detail panel, linking
   down to the #gsj-reviews section.

   This injects into a THIRD-PARTY app's rendered DOM, so it is
   written to fail silently and never damage the page:

     • The anchor is found by its visible text ("Share"), never by
       class. The panel's classes are styled-components hashes
       (sc-eLkKFx kAnWWj) that change on every Nivoda build; any
       selector built on them would break instantly.
     • A MutationObserver scoped to the app's own block waits for
       the anchor. It is NOT a global event handler.
     • Hard timeout. After it elapses the observer disconnects for
       good — no retry loop, no permanently running observer.
     • If the anchor never appears, the script does nothing at all:
       no node is created, so a partial widget can never be left
       behind.
     • Everything is wrapped in try/catch. A failure here must
       never surface as a page error.
     • Injection is idempotent, guarded by element id.

   Tested against the live app: an injected sibling survived both a
   metal change (14K→18K) and a shape change (Round→Princess) with
   zero React errors. When Nivoda eventually changes their markup
   the badge simply stops appearing.
   ============================================================ */
(function () {
  'use strict';

  var BADGE_ID = 'gsj-nivoda-badge';
  var ANCHOR_TEXT = 'share';
  var TIMEOUT_MS = 20000;   /* the app can take ~14s to mount on a cold load */
  var DEBOUNCE_MS = 200;

  function appRoot() {
    return document.querySelector('[id^="shopify-block-"][id*="nivoda"]');
  }

  /* The Share control whose text is exactly the anchor word.

     It is NOT a leaf — it wraps an icon plus a label, and the same text
     matches at two nesting levels. We walk in document order (outermost
     first) and take the first match whose parent holds more than one
     child. That is the control sitting in the panel row alongside
     "Style …", which is where the badge belongs. The inner match lives in
     a single-child wrapper; inserting there would land inside the Share
     control itself. */
  function findAnchor(root) {
    var all = root.getElementsByTagName('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      var txt = (el.textContent || '').trim().toLowerCase();
      if (txt !== ANCHOR_TEXT) continue;
      if (el.parentElement && el.parentElement.children.length > 1) return el;
    }
    return null;
  }

  function buildBadge() {
    var cfg = window.GSJNivodaBadge || {};
    if (!cfg.src) return null;
    var a = document.createElement('a');
    a.id = BADGE_ID;
    a.className = 'gsj-nivoda-badge';
    a.href = '#gsj-reviews';
    a.setAttribute('aria-label', cfg.label || 'Read our customer reviews');
    /* Rendered width is a merchant setting; the width/height below are the
       image's intrinsic size and exist only to reserve space. */
    if (cfg.displayWidth) {
      try { a.style.setProperty('--gsj-badge-width', cfg.displayWidth + 'px'); }
      catch (e) { /* older browsers keep the stylesheet default */ }
    }
    var img = document.createElement('img');
    img.src = cfg.src;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.loading = 'lazy';
    if (cfg.width) img.width = cfg.width;
    if (cfg.height) img.height = cfg.height;
    a.appendChild(img);
    return a;
  }

  function inject() {
    var root = appRoot();
    if (!root) return false;
    if (root.querySelector('#' + BADGE_ID)) return true;   /* already placed */
    var anchor = findAnchor(root);
    if (!anchor || !anchor.parentElement) return false;
    var badge = buildBadge();
    if (!badge) return false;
    anchor.parentElement.insertBefore(badge, anchor);
    return true;
  }

  function start() {
    var root = appRoot();
    if (!root || typeof MutationObserver === 'undefined') return;

    try { inject(); } catch (e) { return; }

    var timer = null;
    var observer = new MutationObserver(function () {
      if (timer) return;
      timer = window.setTimeout(function () {
        timer = null;
        try { inject(); } catch (e) { /* never surface */ }
      }, DEBOUNCE_MS);
    });

    try {
      observer.observe(root, { childList: true, subtree: true });
    } catch (e) {
      return;
    }

    /* Hard stop. Bounded lifetime, whether or not we ever succeeded. */
    window.setTimeout(function () {
      try {
        observer.disconnect();
        if (timer) { window.clearTimeout(timer); timer = null; }
      } catch (e) { /* nothing to do */ }
    }, TIMEOUT_MS);
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  } catch (e) { /* never surface */ }
})();
